import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTimezone } from "@/contexts/TimezoneContext";
import { convertShowTimes, BASE_TZ, zoneLabel } from "@/lib/timezone";
import TimezoneSelector from "@/components/TimezoneSelector";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ExternalLink, Plus, X, UserPlus, Upload, Radio, Handshake } from "lucide-react";
import { CollabBadge, CollabCheckbox } from "@/components/CollabBadge";

const DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const HOURS = [];
for (let h = 2; h <= 24; h++) HOURS.push(h);
const HOURS_FULL = [];
for (let h = 0; h <= 24; h++) HOURS_FULL.push(h);

// Prime time: 6:00 PM – 11:00 PM EST (18:00-23:00)
const PRIME_START = 18;
const PRIME_END = 23;
function isPrimeHour(h) { return h >= PRIME_START && h < PRIME_END; }
function isPrimeShow(s) {
  // Prime-time is defined in the base (EST) timezone; a show remains a
  // prime-time show for a viewer in any timezone even though the wall-clock
  // hour they see differs. Use the preserved base start_time when present.
  const src = s._base_start_time || s.start_time;
  const start = parseInt(src.split(":")[0], 10);
  return start >= PRIME_START && start < PRIME_END;
}

// Daypart labels (EST). Ranges are treated as [start, end) half-open.
//   Morning     06:00 – 12:00
//   Afternoon   12:00 – 18:00
//   Primetime   18:00 – 23:00
//   Late-Night  23:00 – 06:00  (wraps midnight)
function daypartForHour(h) {
  if (h >= 6 && h < 12) return "morning";
  if (h >= 12 && h < 18) return "afternoon";
  if (h >= 18 && h < 23) return "primetime";
  return "late-night";
}
const DAYPART_META = {
  "morning":    { label: "MORNING",    color: "#FFB020", bg: "#2A1F0B" },
  "afternoon":  { label: "AFTERNOON",  color: "#FFD60A", bg: "#2A230B" },
  "primetime":  { label: "PRIME TIME", color: "#FF3B30", bg: "#2A1010" },
  "late-night": { label: "LATE NIGHT", color: "#5AC8FA", bg: "#0B1A2A" },
};
function dayparts(s) {
  // A show may cover multiple dayparts if it's long — return the sorted set.
  const src = s._base_start_time || s.start_time;
  const srcEnd = s._base_end_time || s.end_time;
  if (!src || !srcEnd) return [];
  const [sh, sm] = src.split(":").map(Number);
  const [eh, em] = srcEnd.split(":").map(Number);
  const start = sh * 60 + (sm || 0);
  let end = eh * 60 + (em || 0);
  if (end <= start) end += 24 * 60; // wraps midnight
  const seen = new Set();
  for (let t = start; t < end; t += 30) {
    seen.add(daypartForHour(Math.floor((t % (24 * 60)) / 60)));
  }
  // Return in canonical order
  return ["morning", "afternoon", "primetime", "late-night"].filter((d) => seen.has(d));
}

// Return effective airing days: prefer days_of_week (multi), else day_of_week
// If show is a DAILY special, expand to all 7 days automatically.
function airingDays(s) {
  if (s.is_special && s.special_frequency === "daily") return [0, 1, 2, 3, 4, 5, 6];
  if (Array.isArray(s.days_of_week) && s.days_of_week.length > 0) return s.days_of_week;
  return [s.day_of_week];
}

// --- Per-day / per-date time overrides -------------------------------------
// A show may set `day_times` ({"1":{start,end}}) for recurring per-weekday
// timeslots and `date_overrides` ({"2026-06-20":{start,end}}) for one-off dates.
// Missing entries fall back to the show's default start_time/end_time. All
// times are stored in BASE_TZ (US Eastern).
function isoOfDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function effectiveBaseTime(s, weekday, dateIso) {
  const dv = s.date_overrides || {};
  const dt = s.day_times || {};
  if (dateIso && dv[dateIso] && dv[dateIso].start && dv[dateIso].end)
    return { start: dv[dateIso].start, end: dv[dateIso].end };
  if (weekday != null && dt[String(weekday)] && dt[String(weekday)].start && dt[String(weekday)].end)
    return { start: dt[String(weekday)].start, end: dt[String(weekday)].end };
  return { start: s.start_time, end: s.end_time };
}
function hasVariedTimes(s) {
  return (s.day_times && Object.keys(s.day_times).length > 0) ||
         (s.date_overrides && Object.keys(s.date_overrides).length > 0);
}
// Time for a specific calendar date, converted into the viewer's timezone.
function effectiveDisplayTime(s, date, targetTz) {
  const { start, end } = effectiveBaseTime(s, date.getDay(), isoOfDate(date));
  if (!start || !end) return { start, end };
  if (targetTz && targetTz !== BASE_TZ) {
    const conv = convertShowTimes(date.getDay(), start, end, targetTz);
    return { start: conv.start_time, end: conv.end_time };
  }
  return { start, end };
}
// Expand a show into one placed occurrence per airing weekday, each carrying its
// own per-weekday time, converted independently into targetTz (so a Mon 11pm
// and a Wed 2pm airing can shift to different target days). Used by the grid.
function gridOccurrences(shows, targetTz) {
  const out = [];
  for (const s of shows) {
    if (!s.start_time || !s.end_time) continue;
    const baseDays = (s.is_special && s.special_frequency === "daily")
      ? [0, 1, 2, 3, 4, 5, 6]
      : (Array.isArray(s.days_of_week) && s.days_of_week.length > 0 ? s.days_of_week : [s.day_of_week ?? 0]);
    for (const d of baseDays) {
      const { start, end } = effectiveBaseTime(s, d, null);
      if (!start || !end) continue;
      let day = d, st = start, et = end;
      if (targetTz && targetTz !== BASE_TZ) {
        const conv = convertShowTimes(d, start, end, targetTz);
        day = conv.day; st = conv.start_time; et = conv.end_time;
      }
      out.push({
        ...s,
        day_of_week: day,
        days_of_week: [day],
        _all_days_of_week: baseDays,
        start_time: st,
        end_time: et,
        _base_start_time: start,
        _base_end_time: end,
        _base_day_of_week: d,
      });
    }
  }
  return out;
}

// True if any date in the given month/year with the given day-of-week is on/after `iso`
function dayHasOccurrenceOnOrAfter(dayOfWeek, month, year, iso) {
  const target = new Date(iso);
  if (isNaN(target)) return true;
  const daysInMonth = new Date(year, month, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(year, month - 1, d);
    if (dt.getDay() === dayOfWeek && dt >= target) return true;
  }
  return false;
}

// Determine if a show should appear on the schedule for a given month/year view.
// Rules:
// - premiere_date is in the future relative to the whole viewed month -> hide
// - non-recurring shows only appear in their premiere month
// - season_start / season_end limit the visible window
function showAppearsInMonth(s, month, year) {
  const viewStart = new Date(year, month - 1, 1);
  const viewEnd = new Date(year, month, 0, 23, 59, 59); // last moment of month

  const pd = s.premiere_date ? new Date(s.premiere_date) : null;
  if (pd && !isNaN(pd)) {
    if (!s.is_recurring) {
      // one-off shows only in the exact month
      if (pd < viewStart || pd > viewEnd) return false;
    } else {
      // recurring: only appear on/after the premiere month
      if (viewEnd < pd) return false;
    }
  }
  if (s.season_start) {
    const ss = new Date(s.season_start);
    if (!isNaN(ss) && viewEnd < ss) return false;
  }
  if (s.season_end) {
    const se = new Date(s.season_end);
    if (!isNaN(se) && viewStart > new Date(se.getTime() + 24 * 60 * 60 * 1000)) return false;
  }
  return true;
}

// True if the show's premiere falls inside the currently viewed month
function premiereInMonth(s, month, year) {
  if (!s.premiere_date) return false;
  const pd = new Date(s.premiere_date);
  return pd.getFullYear() === year && (pd.getMonth() + 1) === month;
}

// Return "MMM DD" for a premiere date
function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Consistent color per category (falls back to red)
const CATEGORY_COLORS = {
  "Talk": "#007AFF",
  "Music": "#FF3B30",
  "Sports": "#32D74B",
  "Podcast": "#FFD60A",
  "News": "#AF52DE",
  "Comedy": "#FF9500",
  "Interview": "#5AC8FA",
  "Live Event": "#FF2D92",
};
function categoryColor(cat) {
  if (!cat) return "#FF3B30";
  return CATEGORY_COLORS[cat] || "#FF3B30";
}

function hourLabel(h) {
  const hr = h % 24;
  const ampm = hr < 12 ? "AM" : "PM";
  const display = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${display}:00 ${ampm}`;
}

function timeToMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

// A show is "scheduled" if it has both start_time and end_time set.
function isScheduled(s) {
  return !!(s.start_time && s.end_time);
}

function showsForCell(shows, day, hour, month, year) {
  return shows.filter((s) => {
    if (!isScheduled(s)) return false;
    if (!airingDays(s).includes(day)) return false;
    const start = timeToMinutes(s.start_time);
    const cellStart = hour * 60;
    if (!(start >= cellStart && start < cellStart + 60)) return false;
    // If a premiere date is set on a recurring show, only render this day if
    // an occurrence of that day-of-week in the viewed month is on/after premiere.
    // This ensures recurring episodes never appear BEFORE the premiere date.
    const isRecurringLike = s.is_recurring || (s.is_special && (s.special_frequency === "daily" || s.special_frequency === "weekly"));
    if (s.premiere_date && isRecurringLike && month != null && year != null) {
      if (!dayHasOccurrenceOnOrAfter(day, month, year, s.premiere_date)) return false;
    }
    return true;
  });
}

function HostsBar({ shows }) {
  const hostMap = {};
  const catCount = {};
  shows.forEach((s) => {
    if (s.category) catCount[s.category] = (catCount[s.category] || 0) + 1;
    (s.hosts || []).forEach((h) => {
      if (!hostMap[h]) hostMap[h] = new Set();
      hostMap[h].add(s.title);
    });
  });
  const hosts = Object.keys(hostMap).sort();
  const categories = Object.keys(catCount).sort();
  if (hosts.length === 0 && categories.length === 0) return null;
  return (
    <div className="mb-10 grid md:grid-cols-2 gap-6" data-testid="hosts-bar">
      {hosts.length > 0 && (
        <div className="surface-card p-6">
          <div className="label-eyebrow mb-4 text-[var(--ff-red)]">▮ HOSTS ON AIR · {hosts.length}</div>
          <div className="flex flex-wrap gap-2">
            {hosts.map((h) => (
              <Link key={h} to={`/hosts/${encodeURIComponent(h)}`} className="flex items-center gap-2 bg-[var(--ff-bg)] border border-[var(--ff-border)] px-3 py-2 hover:border-[var(--ff-red)] transition-colors" data-testid={`host-tag-${h.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-7 h-7 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-xs">
                  {h.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </div>
                <div className="leading-tight">
                  <div className="font-bold text-sm">{h}</div>
                  <div className="text-[0.65rem] text-[var(--ff-text-dim)] font-mono-ui">
                    {[...hostMap[h]].slice(0, 2).join(" · ")}
                    {hostMap[h].size > 2 ? ` +${hostMap[h].size - 2}` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      {categories.length > 0 && (
        <div className="surface-card p-6">
          <div className="label-eyebrow mb-4 text-[var(--ff-red)]">▮ CATEGORIES · {categories.length}</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <span key={c} className="font-mono-ui text-xs uppercase tracking-[0.15em] px-3 py-2 border flex items-center gap-2" style={{ borderColor: categoryColor(c) }} data-testid={`category-tag-${c.toLowerCase()}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: categoryColor(c) }}></span>
                {c}
                <span className="text-[var(--ff-text-dim)] normal-case">({catCount[c]})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AddShowModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", days_of_week: [1], start_time: "20:00", end_time: "21:00",
    youtube_url: "", description: "", category: "", hosts: "",
    is_recurring: true, is_premiere: false, premiere_date: "",
    is_finale: false, finale_date: "", season_number: "", season_start: "", season_end: "",
    is_special: false, special_duration_hours: "", thumbnail_url: "", special_frequency: "one_day", is_collab: false,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const toggleDay = (d) => {
    const cur = form.days_of_week;
    setForm({ ...form, days_of_week: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort() });
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, thumbnail_url: `${API_BASE.replace(/\/api$/, "")}${data.url}` }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploadingLogo(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (form.days_of_week.length === 0) return toast.error("Pick at least one day");
    setSaving(true);
    try {
      await api.post("/shows", {
        ...form,
        day_of_week: form.days_of_week[0], // legacy first-day fallback
        hosts: form.hosts.split(",").map((s) => s.trim()).filter(Boolean),
        season_number: form.season_number ? Number(form.season_number) : null,
        special_duration_hours: form.special_duration_hours ? Number(form.special_duration_hours) : null,
        month: null, year: null,
      });
      toast.success("Show added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur p-4 overflow-y-auto" onClick={onClose} data-testid="add-show-modal">
      <form onSubmit={submit} className="surface-card max-w-lg w-full p-8 space-y-3 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <div>
            <div className="label-eyebrow text-[var(--ff-red)] mb-1">▮ QUICK ADD</div>
            <h3 className="font-display text-3xl">New Show</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--ff-text-dim)] hover:text-white"><X size={20}/></button>
        </div>
        <input required placeholder="Show title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2" data-testid="quick-show-title" />
        <input list="quick-cats" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2" data-testid="quick-show-category" />
        <datalist id="quick-cats">
          {Object.keys(CATEGORY_COLORS).map((c) => <option key={c} value={c} />)}
        </datalist>
        <input placeholder="Hosts (comma-separated)" value={form.hosts} onChange={(e) => setForm({ ...form, hosts: e.target.value })} className="w-full px-3 py-2" data-testid="quick-show-hosts" />
        <div>
          <label className="label-eyebrow block mb-2">Days on air</label>
          <div className="grid grid-cols-7 gap-1" data-testid="quick-show-days">
            {DAYS.map((d, i) => {
              const on = form.days_of_week.includes(i);
              return (
                <button
                  key={d} type="button" onClick={() => toggleDay(i)}
                  data-testid={`quick-show-day-${i}`}
                  className={`font-mono-ui text-xs uppercase tracking-[0.1em] px-2 py-2 border transition-colors ${on ? "bg-[var(--ff-red)] text-white border-[var(--ff-red)]" : "bg-[var(--ff-bg)] text-[var(--ff-text-dim)] border-[var(--ff-border)] hover:text-white"}`}
                >{d}</button>
              );
            })}
          </div>
          <p className="text-[0.65rem] text-[var(--ff-text-dim)] mt-1 font-mono-ui">Pick one or more days.</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="px-3 py-2" data-testid="quick-show-start" />
          <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="px-3 py-2" data-testid="quick-show-end" />
        </div>
        <div className="space-y-2 border-t border-[var(--ff-border)] pt-3">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="quick-show-recurring-label">
            <input type="checkbox" checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="quick-show-recurring" />
            <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Recurring weekly episode</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_premiere} onChange={(e) => setForm({ ...form, is_premiere: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="quick-show-premiere" />
            <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Premiere ★</span>
          </label>
          {form.is_premiere && (
            <input type="date" value={form.premiere_date} onChange={(e) => setForm({ ...form, premiere_date: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="quick-show-premiere-date" />
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_finale} onChange={(e) => setForm({ ...form, is_finale: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="quick-show-finale" />
            <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Season finale ⬢</span>
          </label>
          {form.is_finale && (
            <input type="date" value={form.finale_date} onChange={(e) => setForm({ ...form, finale_date: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="quick-show-finale-date" />
          )}
          <CollabCheckbox checked={form.is_collab} onChange={(v) => setForm({ ...form, is_collab: v })} testId="quick-show-collab" />
          <div className="pt-2 border-t border-[var(--ff-border)] space-y-2">
            <div className="label-eyebrow">Season (optional)</div>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" min="1" placeholder="Season #" value={form.season_number} onChange={(e) => setForm({ ...form, season_number: e.target.value })} className="px-3 py-2 text-sm" data-testid="quick-show-season-number" />
              <input type="date" value={form.season_start} onChange={(e) => setForm({ ...form, season_start: e.target.value })} className="px-3 py-2 text-sm" data-testid="quick-show-season-start" />
              <input type="date" value={form.season_end} onChange={(e) => setForm({ ...form, season_end: e.target.value })} className="px-3 py-2 text-sm" data-testid="quick-show-season-end" />
            </div>
          </div>
          <div className="pt-2 border-t border-[var(--ff-border)] space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_special} onChange={(e) => setForm({ ...form, is_special: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="quick-show-special" />
              <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Special Episode ✦</span>
            </label>
            {form.is_special && (
              <div className="space-y-2">
                <div className="flex gap-2 flex-wrap">
                  {[2, 4, 6, 12, 24].map((h) => (
                    <button key={h} type="button" onClick={() => setForm({ ...form, special_duration_hours: h })}
                      className={`font-mono-ui text-xs uppercase tracking-[0.1em] px-3 py-1.5 border transition-colors ${Number(form.special_duration_hours) === h ? "bg-[#AF52DE] text-white border-[#AF52DE]" : "border-[var(--ff-border)] text-[var(--ff-text-dim)] hover:text-white"}`}
                      data-testid={`quick-show-special-preset-${h}`}>
                      {h}H
                    </button>
                  ))}
                </div>
                <input type="number" min="1" max="72" placeholder="Custom hours" value={form.special_duration_hours} onChange={(e) => setForm({ ...form, special_duration_hours: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="quick-show-special-hours" />
              </div>
            )}
          </div>
        </div>
        <input placeholder="YouTube URL (optional)" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className="w-full px-3 py-2" />
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2" />
        <div>
          <label className="label-eyebrow block mb-2">Show Logo</label>
          <div className="flex items-start gap-3">
            {form.thumbnail_url ? (
              <img src={form.thumbnail_url} alt="preview" className="w-14 h-14 object-cover border border-[var(--ff-border)]" data-testid="quick-show-logo-preview" />
            ) : (
              <div className="w-14 h-14 bg-[var(--ff-bg)] border border-dashed border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-text-dim)] text-xs">LOGO</div>
            )}
            <label className="btn-ghost text-xs cursor-pointer flex-1 justify-center">
              <Upload size={12} /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
              <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo} onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="quick-show-logo-upload" />
            </label>
          </div>
        </div>
        <button disabled={saving} className="btn-primary w-full justify-center" data-testid="quick-show-save">{saving ? "Adding..." : "Add Show →"}</button>
      </form>
    </div>
  );
}

function AddHostModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", title: "", bio: "", social_url: "", avatar_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, avatar_url: `${API_BASE.replace(/\/api$/, "")}${data.url}` }));
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/hosts", form);
      toast.success("Host added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur p-4" onClick={onClose} data-testid="add-host-modal">
      <form onSubmit={submit} className="surface-card max-w-lg w-full p-8 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <div>
            <div className="label-eyebrow text-[var(--ff-red)] mb-1">▮ QUICK ADD</div>
            <h3 className="font-display text-3xl">New Host</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--ff-text-dim)] hover:text-white"><X size={20}/></button>
        </div>
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2" data-testid="quick-host-name" />
        <input placeholder="Title (e.g. Radio DJ)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2" data-testid="quick-host-title" />
        <div className="flex items-start gap-3">
          {form.avatar_url ? (
            <img src={form.avatar_url} alt="preview" className="w-14 h-14 object-cover border border-[var(--ff-border)]" />
          ) : (
            <div className="w-14 h-14 bg-[var(--ff-bg)] border border-dashed border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-text-dim)] text-xs">IMG</div>
          )}
          <label className="btn-ghost text-xs cursor-pointer flex-1 justify-center">
            <Upload size={12} /> {uploading ? "Uploading..." : "Upload Avatar"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="quick-host-avatar" />
          </label>
        </div>
        <input placeholder="Social / website URL" value={form.social_url} onChange={(e) => setForm({ ...form, social_url: e.target.value })} className="w-full px-3 py-2" />
        <textarea placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="w-full px-3 py-2" data-testid="quick-host-bio" />
        <button disabled={saving} className="btn-primary w-full justify-center" data-testid="quick-host-save">{saving ? "Adding..." : "Add Host →"}</button>
      </form>
    </div>
  );
}

// Does this specific date fall inside a show's season/premiere window and airing days?
function showAirsOnDate(s, date) {
  if (!s.start_time || !s.end_time) return false;
  const iso = isoOfDate(date);
  const dv = s.date_overrides || {};
  const hasOverride = !!(dv[iso] && dv[iso].start && dv[iso].end);
  // An explicit one-off date override always airs that date (admin picked it).
  if (hasOverride) return true;
  const dayOfWeek = date.getDay();
  if (!airingDays(s).includes(dayOfWeek)) return false;
  if (s.premiere_date) {
    const pd = new Date(s.premiere_date);
    if (!isNaN(pd) && date < pd) return false;
    if (!s.is_recurring && !s.is_special && date.toDateString() !== pd.toDateString()) return false;
  }
  if (s.finale_date) {
    const fd = new Date(s.finale_date);
    if (!isNaN(fd) && date > fd) return false;
  }
  if (s.season_start) {
    const ss = new Date(s.season_start);
    if (!isNaN(ss) && date < ss) return false;
  }
  if (s.season_end) {
    const se = new Date(s.season_end);
    if (!isNaN(se) && date > se) return false;
  }
  return true;
}

function CalendarView({ shows, month, year, onSelectShow, timezone }) {
  // Build a 6-week calendar grid starting from the Sunday on/before the 1st
  const first = new Date(year, month - 1, 1);
  const startOffset = first.getDay(); // 0-6
  const startDate = new Date(year, month - 1, 1 - startOffset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    days.push(d);
  }
  const today = new Date();
  const isToday = (d) => d.toDateString() === today.toDateString();
  const isCurrentMonth = (d) => d.getMonth() === month - 1;
  return (
    <div data-testid="calendar-view">
      <div className="grid grid-cols-7 gap-px bg-[var(--ff-border)] mb-px">
        {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
          <div key={d} className="bg-[var(--ff-surface)] py-3 text-center label-eyebrow">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-[var(--ff-border)]">
        {days.map((d, i) => {
          const dayShows = shows.filter((s) => showAirsOnDate(s, d))
            .map((s) => ({ s, t: effectiveDisplayTime(s, d, timezone) }))
            .sort((a, b) => (a.t.start || "").localeCompare(b.t.start || ""));
          const dim = !isCurrentMonth(d);
          return (
            <div
              key={i}
              className={`min-h-[130px] p-2 flex flex-col ${dim ? "bg-[var(--ff-bg)]" : "bg-[var(--ff-surface)]"} ${isToday(d) ? "ring-2 ring-inset ring-[var(--ff-red)]" : ""}`}
              data-testid={`cal-cell-${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,"0")}-${d.getDate().toString().padStart(2,"0")}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`font-display text-lg leading-none ${dim ? "text-[var(--ff-text-dim)]" : "text-white"}`}>{d.getDate()}</div>
                {isToday(d) && <div className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-red)]">TODAY</div>}
              </div>
              <div className="space-y-0.5 overflow-hidden flex-1">
                {dayShows.slice(0, 4).map(({ s, t }) => {
                  const color = categoryColor(s.category);
                  const isOverride = !!(s.date_overrides && s.date_overrides[isoOfDate(d)]);
                  return (
                    <button
                      key={s.id}
                      onClick={() => onSelectShow({ ...s, start_time: t.start, end_time: t.end, _occ_date: isoOfDate(d) })}
                      className="w-full text-left text-[0.65rem] font-mono-ui px-1.5 py-1 truncate flex items-center gap-1 hover:brightness-125 transition-all"
                      style={{ background: color, color: "white" }}
                      data-testid={`cal-show-${s.id}-${d.getDate()}`}
                      title={isOverride ? "Special one-off time" : undefined}
                    >
                      {s.is_collab && <Handshake size={10} className="shrink-0" aria-label="Collaboration" data-testid={`cal-collab-${s.id}-${d.getDate()}`} />}
                      {s.is_special && <span>✦</span>}
                      {s.is_premiere && <span>★</span>}
                      {s.is_finale && <span>⬢</span>}
                      {isOverride && <span title="One-off time">◆</span>}
                      <span className="opacity-90">{t.start}</span>
                      <span className="truncate font-bold">{s.title}</span>
                    </button>
                  );
                })}
                {dayShows.length > 4 && (
                  <div className="text-[0.6rem] font-mono-ui text-[var(--ff-text-dim)] text-center">
                    +{dayShows.length - 4} MORE
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Collaboration shows live in their own section under the main schedule (never in the grid/calendar).
function CollabSection({ shows, timezone, onSelectShow }) {
  if (!shows.length) return null;
  return (
    <section className="mt-14" data-testid="schedule-collab-section">
      <div className="mb-4 pb-3 border-b border-[#5AC8FA]/30">
        <div className="label-eyebrow text-[#5AC8FA] flex items-center gap-2" data-testid="schedule-collab-heading">
          <Handshake size={12} /> ▮ COLLABORATIONS · {shows.length}
        </div>
        <p className="text-sm text-[var(--ff-text-dim)] mt-1">Shows we air with partners. Not Freshy Fresh Productions LLC property.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {shows.map((s) => {
          const occ = gridOccurrences([s], timezone)
            .sort((a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time));
          return (
            <button
              key={s.id}
              onClick={() => onSelectShow(occ[0] || s)}
              className="bg-[var(--ff-surface)] hover:bg-[var(--ff-surface-hover)] text-left p-5 border border-[var(--ff-border)] border-l-4 transition-colors"
              style={{ borderLeftColor: categoryColor(s.category) }}
              data-testid={`schedule-collab-${s.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <CollabBadge size="xs" testId={`schedule-collab-badge-${s.id}`} />
                {s.category && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em]" style={{ color: categoryColor(s.category) }}>{s.category}</span>}
              </div>
              <div className="font-display text-xl truncate">{s.title}</div>
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`schedule-collab-times-${s.id}`}>
                {occ.map((o) => (
                  <span key={`${o._base_day_of_week}-${o.start_time}`} className="font-mono-ui text-[0.65rem] uppercase tracking-[0.1em] px-2 py-1 border border-[var(--ff-border)]">
                    {DAYS[o.day_of_week]} {o.start_time}–{o.end_time}
                  </span>
                ))}
              </div>
              {s.hosts?.length > 0 && (
                <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)] mt-3 truncate">{s.hosts.join(" · ")}</div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default function Schedule() {
  const { user } = useAuth();
  const { timezone, isBase } = useTimezone();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [shows, setShows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(null); // 'show' | 'host' | null
  const [view, setView] = useState("grid"); // 'grid' | 'calendar'

  const load = async () => {
    const { data } = await api.get("/shows", { params: { month, year } });
    setShows(data);
  };

  useEffect(() => { load(); }, [month, year]);

  // Filter shows for the currently viewed month based on premiere/season rules,
  // and drop drafts (no times/days). These base (US Eastern) shows drive the
  // stats + calendar; the weekly grid uses per-weekday occurrences below.
  const visibleShows = useMemo(
    () => shows.filter((s) => isScheduled(s) && showAppearsInMonth(s, month, year)),
    [shows, month, year]
  );
  const ownShows = useMemo(() => visibleShows.filter((s) => !s.is_collab), [visibleShows]);
  const collabShows = useMemo(() => visibleShows.filter((s) => s.is_collab), [visibleShows]);

  // Per-weekday occurrences (each with its own effective time), converted into
  // the viewer's timezone independently. This is what the weekly grid renders.
  const gridOcc = useMemo(() => gridOccurrences(ownShows, timezone), [ownShows, timezone]);

  // In non-base timezones a show can land at any hour of the day, so expand the
  // grid to cover a full 24h. In base timezone we keep the original 2 AM – 12 AM
  // frame to match the network's live-broadcast window.
  const gridHours = isBase ? HOURS : HOURS_FULL;

  const monthName = useMemo(() => new Date(year, month - 1).toLocaleString("en-US", { month: "long" }), [month, year]);

  const navMonth = (dir) => {
    let m = month + dir, y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m); setYear(y);
  };

  const isAdmin = ["founder", "executive"].includes(user?.role);

  return (
    <div data-testid="schedule-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="label-eyebrow mb-4 text-[var(--ff-red)]">▮ BROADCAST / WEEKLY SCHEDULE</div>
          <h1 className="font-display text-5xl lg:text-7xl">WHAT'S ON.</h1>
          <p className="text-[var(--ff-text-dim)] mt-4 max-w-2xl">
            Times shown in <span className="text-white font-mono-ui uppercase tracking-[0.15em]" data-testid="schedule-tz-label">{zoneLabel(timezone)}</span>
            {!isBase && <span className="text-[var(--ff-text-dim)]"> · Baseline: US Eastern (EST/EDT)</span>}
            . Shows stream live on the Freshy Fresh YouTube network. Sun – Sat / 2:00 AM – 12:00 AM ET.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <TimezoneSelector />
          <div className="flex items-center gap-3 border border-[var(--ff-border)] p-2" data-testid="month-selector">
            <button onClick={() => navMonth(-1)} className="btn-ghost py-2 px-3" data-testid="prev-month-btn"><ChevronLeft size={16} /></button>
            <div className="px-6 font-mono-ui text-sm uppercase tracking-[0.2em] min-w-[180px] text-center">
              {monthName} {year}
            </div>
            <button onClick={() => navMonth(1)} className="btn-ghost py-2 px-3" data-testid="next-month-btn"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex justify-end mb-4">
        <div className="inline-flex border border-[var(--ff-border)]" data-testid="view-toggle">
          <button onClick={() => setView("grid")} data-testid="view-grid-btn"
            className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors ${view === "grid" ? "bg-white text-black" : "text-[var(--ff-text-dim)] hover:text-white"}`}>
            Weekly Grid
          </button>
          <button onClick={() => setView("calendar")} data-testid="view-calendar-btn"
            className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors ${view === "calendar" ? "bg-white text-black" : "text-[var(--ff-text-dim)] hover:text-white"}`}>
            Calendar
          </button>
        </div>
      </div>

      {/* Network stats bar + admin quick-add */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10 py-4 border-y border-[var(--ff-border)]">
        <div className="flex items-center gap-6 font-mono-ui text-xs uppercase tracking-[0.18em]" data-testid="network-stats">
          <div className="flex items-center gap-2">
            <Radio size={14} className="text-[var(--ff-red)]" />
            <span className="text-[var(--ff-text-dim)]">Total Shows:</span>
            <span className="font-display text-2xl leading-none text-white" data-testid="show-count">{ownShows.length}</span>
          </div>
          {collabShows.length > 0 && (
            <>
              <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
              <div>
                <span className="text-[var(--ff-text-dim)]">Collabs:</span>{" "}
                <span className="text-[#5AC8FA]" data-testid="collab-count">{collabShows.length}</span>
              </div>
            </>
          )}
          <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
          <div>
            <span className="text-[var(--ff-text-dim)]">Prime Time <span className="text-[0.6rem]">(6-11pm ET)</span>:</span>{" "}
            <span className="text-white" data-testid="primetime-count">{ownShows.filter(isPrimeShow).length}</span>
          </div>
          <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
          <div>
            <span className="text-[var(--ff-text-dim)]">Premieres:</span>{" "}
            <span className="text-white" data-testid="premiere-count">{ownShows.filter((s) => s.is_premiere).length}</span>
          </div>
          <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
          <div>
            <span className="text-[var(--ff-text-dim)]">Specials:</span>{" "}
            <span className="text-white" data-testid="special-count">{ownShows.filter((s) => s.is_special).length}</span>
          </div>
          <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
          <div>
            <span className="text-[var(--ff-text-dim)]">Categories:</span>{" "}
            <span className="text-white" data-testid="category-count">{new Set(ownShows.filter((s) => s.category).map((s) => s.category)).size}</span>
          </div>
          <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
          <div>
            <span className="text-[var(--ff-text-dim)]">Hosts:</span>{" "}
            <span className="text-white" data-testid="host-count">{new Set(ownShows.flatMap((s) => s.hosts || [])).size}</span>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-3" data-testid="admin-quick-add">
            <button onClick={() => setShowModal("show")} className="btn-primary text-xs" data-testid="quick-add-show-btn">
              <Plus size={12}/> Add Show
            </button>
            <button onClick={() => setShowModal("host")} className="btn-ghost text-xs" data-testid="quick-add-host-btn">
              <UserPlus size={12}/> Add Host
            </button>
          </div>
        )}
      </div>

      {/* Upcoming premiere notices for hidden shows */}
      {shows.filter((s) => s.is_premiere && s.premiere_date && new Date(s.premiere_date) > new Date(year, month, 0) && !showAppearsInMonth(s, month, year)).length > 0 && (
        <div className="mb-6 surface-card border-l-4 border-l-[#FFD60A] p-4 flex items-start gap-3" data-testid="upcoming-premieres">
          <span className="text-[#FFD60A] font-display text-2xl leading-none">★</span>
          <div className="flex-1">
            <div className="label-eyebrow mb-1 text-[#FFD60A]">▮ UPCOMING IN FUTURE MONTHS</div>
            <div className="text-sm text-[var(--ff-text-dim)]">
              {shows.filter((s) => s.is_premiere && s.premiere_date && new Date(s.premiere_date) > new Date(year, month, 0) && !showAppearsInMonth(s, month, year)).map((s) => (
                <span key={s.id} className="mr-3 font-mono-ui text-xs">
                  <span className="text-white">{s.title}</span> — premieres {shortDate(s.premiere_date)}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <HostsBar shows={ownShows} />

      {view === "calendar" ? (
        <CalendarView shows={ownShows} month={month} year={year} onSelectShow={setSelected} timezone={timezone} />
      ) : (
      <div className="overflow-x-auto" data-testid="schedule-grid-wrapper">
        <div className="min-w-[900px]">
          <div className="schedule-grid">
            <div className="schedule-cell !min-h-[44px] flex items-center justify-center label-eyebrow !bg-[var(--ff-surface)]">TIME</div>
            {DAYS.map((d) => (
              <div key={d} className="schedule-cell !min-h-[44px] flex items-center justify-center font-mono-ui text-xs !bg-[var(--ff-surface)] font-bold tracking-[0.2em]">
                {d}
              </div>
            ))}
            {gridHours.map((h) => {
              const dp = daypartForHour(h);
              const dpMeta = DAYPART_META[dp];
              return (
              <React.Fragment key={h}>
                <div
                  className="schedule-cell flex items-start justify-center pt-2 font-mono-ui text-[0.7rem]"
                  style={{ background: dpMeta.bg, color: dpMeta.color }}
                  data-testid={`hour-rail-${h}`}
                >
                  <div className="text-center leading-tight">
                    {hourLabel(h)}
                    <div className="text-[0.55rem] mt-0.5 opacity-90 whitespace-nowrap">{dpMeta.label}</div>
                  </div>
                </div>
                {DAYS.map((_, day) => {
                  const cellShows = showsForCell(gridOcc, day, h, month, year);
                  return (
                    <div
                      key={`${day}-${h}`}
                      className="schedule-cell"
                      style={{ background: dp === "primetime" ? "#1A0B0B" : dp === "morning" ? "#1A140B" : dp === "afternoon" ? "#1A170B" : "#0B1116" }}
                    >
                      {cellShows.map((s) => {
                        const startMin = timeToMinutes(s.start_time);
                        const endMin = timeToMinutes(s.end_time);
                        const duration = (endMin > startMin ? endMin - startMin : (24 * 60 - startMin) + endMin);
                        const offset = startMin - h * 60;
                        const color = categoryColor(s.category);
                        const showsPremiereRibbon = s.is_premiere && premiereInMonth(s, month, year);
                        return (
                          <div
                            key={`${s.id}-${day}`}
                            onClick={() => setSelected(s)}
                            className="schedule-show"
                            style={{
                              top: `${(offset / 60) * 56}px`,
                              height: `${Math.max((duration / 60) * 56 - 4, 30)}px`,
                              background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                              borderLeftColor: s.is_finale ? "#FF3B30" : s.is_premiere ? "#FFD60A" : "white",
                            }}
                            data-testid={`show-${s.id}`}
                          >
                            <div className="font-bold truncate flex items-center gap-1">
                              {s.is_collab && <Handshake size={11} className="shrink-0" aria-label="Collaboration" data-testid={`week-collab-${s.id}-${day}`} />}
                              {s.is_special && <span className="text-[#AF52DE]">✦</span>}
                              {s.is_premiere && <span className="text-[#FFD60A]">★</span>}
                              {s.is_finale && <span className="text-white">⬢</span>}
                              {s.title}
                              {s.is_special && s.special_duration_hours && (
                                <span className="text-[0.55rem] font-mono-ui text-[#AF52DE] ml-1">{s.special_duration_hours}H</span>
                              )}
                            </div>
                            <div className="opacity-90 text-[0.65rem] flex items-center justify-between gap-2">
                              <span>{s.start_time}–{s.end_time}</span>
                              <span className="flex items-center gap-1">
                                {isPrimeShow(s) && (
                                  <span className="text-[0.5rem] font-bold px-1 py-px bg-[var(--ff-red)] text-white tracking-[0.1em]" title="Prime Time">PT</span>
                                )}
                                {s.category && <span className="uppercase tracking-[0.1em] opacity-80">{s.category}</span>}
                              </span>
                            </div>
                            {showsPremiereRibbon && (
                              <div className="mt-0.5 text-[0.6rem] font-bold text-[#FFD60A] tracking-[0.1em] truncate">
                                ★ STARTS {shortDate(s.premiere_date).toUpperCase()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      )}

      <CollabSection shows={collabShows} timezone={timezone} onSelectShow={setSelected} />

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur p-4" onClick={() => setSelected(null)} data-testid="show-modal">
          <div className="surface-card max-w-xl w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {selected.category && (
                <span className="w-3 h-3 rounded-full" style={{ background: categoryColor(selected.category) }}></span>
              )}
              <div className="label-eyebrow text-[var(--ff-red)]">
                {(selected._all_days_of_week || airingDays(selected)).map((d) => DAY_FULL[d].slice(0, 3).toUpperCase()).join(" · ")} / {selected.start_time}–{selected.end_time}
                {selected.category && <span className="ml-3 text-[var(--ff-text-dim)]">/ {selected.category.toUpperCase()}</span>}
              </div>
              {isPrimeShow(selected) && (
                <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border border-[var(--ff-red)] text-[var(--ff-red)]" data-testid="modal-primetime-badge">▮ PRIME TIME</span>
              )}
              {dayparts(selected).filter((d) => d !== "primetime").map((d) => {
                const m = DAYPART_META[d];
                return (
                  <span
                    key={d}
                    className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border"
                    style={{ borderColor: m.color, color: m.color }}
                    data-testid={`modal-daypart-${d}`}
                  >▮ {m.label}</span>
                );
              })}
              {selected.is_collab && <CollabBadge size="lg" testId="modal-collab-badge" />}
              {selected.is_special && (
                <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border border-[#AF52DE] text-[#AF52DE]" data-testid="modal-special-badge">
                  ✦ {selected.special_frequency === "daily" ? "DAILY " : selected.special_frequency === "weekly" ? "WEEKLY " : ""}SPECIAL
                  {selected.special_duration_hours ? ` · ${selected.special_duration_hours}H` : ""}
                </span>
              )}
              {selected.is_premiere && (
                <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border border-[#FFD60A] text-[#FFD60A]" data-testid="modal-premiere-badge">★ PREMIERE{selected.premiere_date ? ` · ${selected.premiere_date}` : ""}</span>
              )}
              {selected.is_finale && (
                <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border border-[var(--ff-red)] text-[var(--ff-red)]" data-testid="modal-finale-badge">⬢ FINALE{selected.finale_date ? ` · ${selected.finale_date}` : ""}</span>
              )}
              {selected.season_number && (
                <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border border-[var(--ff-border)] text-[var(--ff-text-dim)]">SEASON {selected.season_number}</span>
              )}
              {selected.is_recurring && (
                <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border border-[var(--ff-border)] text-[var(--ff-text-dim)]" data-testid="modal-recurring-badge">↻ WEEKLY</span>
              )}
            </div>
            <h3 className="font-display text-3xl mb-3">{selected.title}</h3>
            {hasVariedTimes(selected) && (() => {
              const bs = shows.find((x) => x.id === selected.id) || selected;
              return (
              <div className="mb-4 border border-[var(--ff-border)] bg-[var(--ff-surface)]/60 p-4" data-testid="modal-varied-times">
                <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ AIRING TIMES (ET)</div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  {airingDays(bs).map((d) => {
                    const t = effectiveBaseTime(bs, d, null);
                    const custom = bs.day_times && bs.day_times[String(d)] && bs.day_times[String(d)].start;
                    return (
                      <div key={d} className="flex items-center justify-between font-mono-ui text-xs">
                        <span className="text-[var(--ff-text-dim)]">{DAY_FULL[d]}</span>
                        <span className={custom ? "text-white" : "text-[var(--ff-text-dim)]"}>
                          {t.start}–{t.end}{!custom && <span className="ml-1 opacity-50">(default)</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {bs.date_overrides && Object.keys(bs.date_overrides).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--ff-border)]">
                    <div className="label-eyebrow text-[#FFD60A] mb-1">◆ ONE-OFF DATES</div>
                    <div className="space-y-1">
                      {Object.entries(bs.date_overrides)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([iso, v]) => (
                          <div key={iso} className="flex items-center justify-between font-mono-ui text-xs">
                            <span className="text-[var(--ff-text-dim)]">{iso}</span>
                            <span className="text-white">{v.start}–{v.end}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })()}
            {selected.hosts && selected.hosts.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2 items-center" data-testid="show-modal-hosts">
                <span className="label-eyebrow">Hosted by:</span>
                {selected.hosts.map((h) => {
                  const isShowrunner = (selected.showrunner || "").trim() === h;
                  return (
                    <Link
                      key={h}
                      to={`/hosts/${encodeURIComponent(h)}`}
                      className={`font-mono-ui text-xs uppercase tracking-[0.15em] px-2 py-1 border transition-colors ${isShowrunner
                        ? "bg-[#FFD60A] text-black border-[#FFD60A] hover:bg-yellow-400"
                        : "border-[var(--ff-red)] text-[var(--ff-red)] hover:bg-[var(--ff-red)] hover:text-white"}`}
                      data-testid={isShowrunner ? "modal-showrunner-tag" : undefined}
                    >
                      {isShowrunner && <span className="mr-1">★</span>}{h}{isShowrunner && <span className="ml-1 opacity-70">· SHOWRUNNER</span>}
                    </Link>
                  );
                })}
              </div>
            )}
            <p className="text-[var(--ff-text-dim)] mb-6">{selected.description}</p>
            <div className="flex gap-3">
              <Link to={`/shows/${selected.id}`} className="btn-ghost" data-testid="modal-view-details">View Full Details →</Link>
              {selected.youtube_url && (
                <a href={selected.youtube_url} target="_blank" rel="noreferrer" className="btn-primary" data-testid="show-youtube-link">
                  <ExternalLink size={14} /> Watch on YouTube
                </a>
              )}
              <button className="btn-ghost" onClick={() => setSelected(null)} data-testid="show-modal-close">Close</button>
            </div>
          </div>
        </div>
      )}

      {showModal === "show" && (
        <AddShowModal onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); load(); }} />
      )}
      {showModal === "host" && (
        <AddHostModal onClose={() => setShowModal(null)} onSaved={() => setShowModal(null)} />
      )}
    </div>
  );
}
